import type {
  GetPerformanceReportStatsParams,
  PerformanceReportStats,
} from "../../api/generated";
import type {
  PerformanceReportTableParams,
  SourceType,
  SummaryCard,
} from "../types";

import { fmtCompactInt, fmtUsdNum } from "../helpers";
import { formatDate, orUndefined, toCampaignIds } from "./queryParams";

/**
 * UI source-type labels → the materialised-view values the stats endpoint
 * filters on. The table endpoint translates these server-side
 * (`get_performance_report` → `sourceTypeFilterMap`); the stats endpoint takes
 * MV values as-is, so the translation has to happen here or the filter silently
 * matches nothing.
 */
const MV_SOURCE_TYPE: Record<SourceType, string> = {
  Links: "referral_link",
  Banners: "marketing_banner",
};

/**
 * Maps the shared table params → the stats query params, so the summary cards
 * always describe the same date range and filter selection as the table.
 * `sourceIds` is not sent: the filter model has no source-id facet yet (it
 * arrives with the drill-in entry point).
 */
export const toStatsQueryParams = (
  params: PerformanceReportTableParams,
): GetPerformanceReportStatsParams => ({
  startDate: formatDate(params.dateRange.startDate),
  endDate: formatDate(params.dateRange.endDate),
  sourceTypes: orUndefined(
    params.filters.sourceType.map((value) => MV_SOURCE_TYPE[value]),
  ),
  landingPages: orUndefined(params.filters.landingPage),
  bannerGroups: orUndefined(params.filters.bannerGroup),
  rebatePrograms: orUndefined(params.filters.rebateProgram),
  countries: orUndefined(params.filters.country),
  campaigns: orUndefined(toCampaignIds(params.filters.campaign)),
});

/**
 * Maps the stats response onto the five headline cards. Money uses `fmtUsdNum`
 * (the same formatter the table and mobile detail views use) so a sub-million
 * value renders as a plain amount rather than `0.0M`. Counts use `fmtCompactInt`
 * so large Clicks/Registered/Active clients values abbreviate with a K/M
 * suffix too (PART-4143), at the same 2-decimal precision as the money cards —
 * intentionally a separate formatter from `fmtInt`, which stays unabbreviated
 * for the table's per-row counts and the mobile row list/detail sheet.
 */
export const mapPerformanceStats = (
  stats: PerformanceReportStats,
): SummaryCard[] => [
  {
    translationKey: "performanceReport_column_clicks",
    value: fmtCompactInt(stats.clicks),
  },
  {
    translationKey: "performanceReport_column_registration",
    value: fmtCompactInt(stats.registration),
  },
  {
    translationKey: "performanceReport_column_activeClients",
    value: fmtCompactInt(stats.activeClients),
  },
  {
    translationKey: "performanceReport_column_tradingVolume",
    value: fmtUsdNum(stats.tradingVolume),
    unit: "USD",
  },
  {
    translationKey: "performanceReport_column_earnings",
    value: fmtUsdNum(stats.earnings),
    unit: "USD",
  },
];

/**
 * Fallback for a failed stats request: the cards keep their translation keys
 * and read zero, so the table stays usable instead of the whole page erroring.
 */
export const ZERO_KPI_CARDS: SummaryCard[] = mapPerformanceStats({
  activeClients: 0,
  clicks: 0,
  earnings: 0,
  registration: 0,
  tradingVolume: 0,
});
